import { useEffect, useState } from 'react'
import axios, { AxiosResponse } from 'axios'
import { ErrorComponent } from '../../components/ErrorComponent/ErrorComponent'
import { Helmet } from 'react-helmet';
import { ContentContainer } from '../../components/ContentContainer/ContentContainer';
import { FIREBASE_STORAGE_URL } from '../../app-settings';

export default function ExternalLinksPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [links, setLinks] = useState<ExternalLinksJson>();

  useEffect(() => {
    const initialize = async () => {
      try {
        setIsLoading(true);
        const response: AxiosResponse<ExternalLinksJson> = await axios
          .get<ExternalLinksJson>(`${FIREBASE_STORAGE_URL}/json/external-links.json`);

        setIsLoading(false);

        if (response.status != 200) {
          return;
        }

        const linksJson = response.data;
        setLinks(linksJson);
      } catch (e) {
        setIsLoading(false);
        setLinks(undefined);
      }
    }

    initialize();
  }, []);

  if (isLoading) {
    return <></>;
  }

  if (!links) {
    return <ErrorComponent title="Unknown Error" message='The external links page encountered an unknown error. Sorry for the inconvenience.' />;
  }

  return (
    <ContentContainer header='Links'>
      <div className="max-w-2xl" >
        <Helmet>
          <title>Links - Materia Hunter</title>
        </Helmet>
        <h2 className="text-2xl font-bold tracking-tight">Empty Deck List Sheet</h2>
        <p className="mt-2 text-lg leading-8">
          Print an empty decklist for use in tournaments.
        </p>
        <h3 className="mt-3 text-lg font-semibold leading-6 text-emphasis group-hover:text-emphasis-secondary">
          <a href={`${FIREBASE_STORAGE_URL}/materiahunter-deck-list.pdf`} target='_blank'>
            Materia Hunter Empty Deck List
          </a>
        </h3>
        <h3 className="mt-3 text-lg font-semibold leading-6 text-emphasis group-hover:text-emphasis-secondary">
          <a href={`${FIREBASE_STORAGE_URL}/materiahunter-legacy-deck-list.pdf`} target='_blank'>
            Materia Hunter Empty Deck List (Legacy)
          </a>
        </h3>
        <h3 className="mt-3 text-lg font-semibold leading-6 text-emphasis group-hover:text-emphasis-secondary">
          <a href="https://fftcg.cdn.sewest.net/OP%20Section/Beginner/fftcg-one-deck-reg-sheet-vsep2024.pdf" target='_blank'>
            Official Empty Deck List (Single Deck)
          </a>
        </h3>
        <h3 className="mt-3 text-lg font-semibold leading-6 text-emphasis group-hover:text-emphasis-secondary">
          <a href="https://fftcg.cdn.sewest.net/OP%20Section/Beginner/fftcg-two-deck-reg-sheet-vsep2024.pdf" target='_blank'>
            Official Empty Deck List (Two Deck)
          </a>
        </h3>

        {
          links &&
          links.sections.map((section, index) => (
            <div className={"mt-24"}>
              <h2 className="text-2xl font-bold tracking-tight">{section.header}</h2>
              <p className="mt-2 text-lg leading-8">
                {section.description}
              </p>
              <div className="mt-4 space-y-8 border-t border-gray-200 pt-4 sm:mt-4 sm:pt-4">
                {section.items.map((item) => (
                  <article key={item.link} className="flex max-w-xl flex-col items-start justify-between">
                    <div className="group relative">
                      <h3 className="mt-3 text-lg font-semibold leading-6 text-emphasis group-hover:text-emphasis-secondary">
                        <a href={item.link}>
                          <span className="absolute inset-0" />
                          {item.name}
                        </a>
                      </h3>
                      <p className="mt-5 line-clamp-3 text-sm leading-6">{item.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))
        }
      </div>
    </ContentContainer>
  )
}

type ExternalLinksJson = {
  sections: ExternalLinksJsonSection[]
}

type ExternalLinksJsonSection = {
  header: string,
  description: string,
  items: ExternalLinksJsonSectionItem[]
}

type ExternalLinksJsonSectionItem = {
  name: string,
  description: string,
  link: string
}